import { camelize, camelizeKeys, decamelizeKeys } from 'humps';

/** All-uppercase keys — acronyms like VAT_ID, CRN, SOC — must survive case transformation. */
const ACRONYM_KEY = /^[A-Z][A-Z0-9_]*$/;

/**
 * humps key converter, direction-agnostic (`convert` is `decamelize` on the way out,
 * `camelize` on the way back).
 *
 * Not exported: it's a detail of *how* the functions below preserve acronyms, not
 * something a caller should pair with `camelizeKeys`/`decamelizeKeys` by hand — a
 * call site that forgot to pass it would silently mangle any all-caps key (e.g.
 * `camelize('SOC')` gives `sOC`) with no type error to catch the omission. Wrapping
 * the pairing here instead means there's only one way to camelize/decamelize a
 * response body in this codebase, not a guard every call site has to remember.
 */
const preserveAcronyms = (key: string, convert: (key: string) => string) =>
  ACRONYM_KEY.test(key) ? key : convert(key);

/**
 * `camelizeKeys`, acronym-preserving. Use for anything read from the API.
 *
 * Typed exactly like humps' own `camelizeKeys(str: object)`/`(str: object[])`
 * overloads (not a generic `<T>(data: T): T`) so callers still get back a loose
 * `object`/`object[]`, the same as before this wrapper existed, and can cast it to
 * whatever typed shape they're expecting.
 */
export function camelizeKeysPreservingAcronyms(data: object[]): object[];
export function camelizeKeysPreservingAcronyms(data: object): object;
export function camelizeKeysPreservingAcronyms(data: object): object {
  return camelizeKeys(data, preserveAcronyms);
}

/** `decamelizeKeys`, acronym-preserving. Use for anything sent to the API. */
export function decamelizeKeysPreservingAcronyms(data: object[]): object[];
export function decamelizeKeysPreservingAcronyms(data: object): object;
export function decamelizeKeysPreservingAcronyms(data: object): object {
  return decamelizeKeys(data, preserveAcronyms);
}

/**
 * How a single wire key appears on a parsed response body — by construction, exactly
 * what `camelizeKeysPreservingAcronyms` does to every key it walks.
 *
 * Use this whenever a key has to be predicted rather than read: telemetry series points
 * carry dynamic metric keys (`P_active_kw_avg`, `soc_pct_avg`, …) that are data, not
 * schema, so the only way to look one up is to derive it from the variable name.
 *
 * Deriving it with a bare `camelize()` is wrong: the acronym guard means `SOC` stays
 * `SOC` on the response while `camelize('SOC')` yields `sOC`. Routing both sides through
 * this one function is what makes that class of mismatch impossible.
 */
export const toResponseKey = (key: string): string => preserveAcronyms(key, camelize);
